<template>
    <section :data-mui-theme="theme" class="slice slice-list slice-list--with-image" aria-label="list" data-testid="list-with-image-slice">
        <div class="slice-content">
            <div class="slice-list--with-image__inner">
                <PrismicImage
                    v-if="isFilled.image(slice.primary.image)"
                    :field="slice.primary.image"
                    :alt="slice.primary.image.alt || ''"
                    class="slice-list--with-image__image"
                    data-testid="list-with-image-image"
                    height="600"
                    width="827"
                />

                <div class="slice-list--with-image__card" data-testid="list-with-image-card">
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.title)"
                        :field="slice.primary.title"
                        class="slice-list--with-image__title mui-title"
                        data-testid="list-with-image-title"
                    />

                    <div
                        v-for="(item, index) in slice.primary.list"
                        :key="index"
                        class="slice-list--with-image__item"
                        :data-testid="`list-with-image-item-${index}`"
                    >
                        <div class="slice-list--with-image__item-header">
                            <div class="slice-list--with-image__item-marker mui-background-darker">
                                <!-- TODO: Add icon back in
                              <VJoyIcon v-if="shouldShowIcon(item.icon)" :name="item.icon" size="small" /> -->
                                <span class="slice-list--with-image__item-index" :data-testid="`list-with-image-item-index-${index}`">
                                    {{ index + 1 }}
                                </span>
                            </div>
                            <div class="slice-list--with-image__item-content">
                                <h3
                                    v-if="item.title"
                                    class="slice-list--with-image__item-title mui-title"
                                    :data-testid="`list-with-image-item-title-${index}`"
                                >
                                    {{ item.title }}
                                </h3>

                                <PrismicRichText
                                    v-if="isFilled.richText(item.description)"
                                    :field="item.description"
                                    class="slice-list--with-image__item-description"
                                    :data-testid="`list-with-image-item-description-${index}`"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {ListSlice} from '@prismicio-types';

    type ListWithImageInZone = Extract<ListSlice, {variation: 'listWithImage'}>;

    const props = defineProps(getSliceComponentProps<ListWithImageInZone>());

    const theme = computed(() => props.slice.primary.theme);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-list--with-image {
        @include mq.screen_md(min) {
            padding: 0 var(--joy-core-spacing-6);
        }

        .slice-content {
            text-align: left;
        }

        &__inner {
            display: flex;
            flex-direction: column;

            @include mq.screen_sm(min) {
                display: grid;
                grid-template-columns: 1fr 1fr 40%;
                align-items: center;
            }
        }

        &__image {
            aspect-ratio: 6 / 9;
            min-width: 0;
            z-index: 1;
            border-radius: var(--joy-core-radius-4);
            width: 100%;
            height: 100%;
            object-fit: cover;

            @include mq.screen_xs(min) {
                aspect-ratio: auto;
            }

            @include mq.screen_sm(min) {
                grid-column: 1 / 3;
            }
        }

        &__card {
            margin-left: var(--joy-core-spacing-2);
            margin-right: var(--joy-core-spacing-2);
            padding: var(--joy-core-spacing-8);
            border-radius: var(--joy-core-radius-5);
            background: var(--joy-color-background-default);
            z-index: 1;
            margin-top: calc(-100% * 6 / 9 * 1.4);

            @include mq.screen_xs(min) {
                margin-top: calc(-100% * 6 / 9 * 0.5);
            }

            @include mq.screen_sm(min) {
                margin-left: -80%;
                margin-top: var(--joy-core-spacing-8);
                margin-bottom: var(--joy-core-spacing-8);
            }

            @include mq.screen_lg(min) {
                margin-left: -25%;
            }
        }

        &__title.slice-list--with-image__title h2 {
            @include text.getFontBySize(secondary-400);
        }

        &__title {
            margin-bottom: var(--joy-core-spacing-9);
        }

        &__item {
            &:not(:last-child) {
                margin-bottom: var(--joy-core-spacing-8);
            }
        }

        &__item-header {
            display: flex;
            align-items: flex-start;
            gap: var(--joy-core-spacing-6);
        }

        &__item-marker {
            padding: var(--joy-core-spacing-3);
            border-radius: 300px;
            color: var(--joy-color-neutral-0);

            @include mq.screen_sm(min) {
                padding: var(--joy-core-spacing-4);
            }

            span {
                position: relative;
                top: 3px;
            }
        }
        &__item-content {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-2);
        }

        &__item-index {
            @include text.getFontBySize(secondary-200);

            @include mq.screen_sm(min) {
                @include text.getFontBySize(secondary-400);
            }
        }

        &__item-description {
            @include text.getFontBySize(primary-300);
            letter-spacing: var(--joy-font-letter-spacing-tight);
            color: var(--joy-color-neutral-40);
            line-height: var(--joy-line-height-large);

            @include mq.screen_sm(min) {
                @include text.getFontBySize(primary-400);
            }
        }
    }
</style>
